import React from "react";
import { Box, Typography, Button, useTheme, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import DataList, { ICourse } from "./courseDataList";
import BasicSelect from "./select";
import { courseType } from "../utlis/courseType";

interface IFormResult {
  courseID: string;
  credits: number;
  missedLecs: number;
  missedTuts: number;
}

interface IDataListRef {
  onClear: () => void;
}

interface IInputFormProps {
  onFormSubmit: (formData: IFormResult) => void;
  onFormClear: () => void;
  bylaw: string;
}

const InputForm : React.FC<IInputFormProps> = (props: IInputFormProps) => {
  const { onFormSubmit, onFormClear, bylaw } = props;

  const [lecturesCount, setLecturesCount] = React.useState<string>("0");
  const [tutsCount, setTutsCount] = React.useState<string>("0");
  const [chosenCourse, setChosenCourse] = React.useState<ICourse>();
  const [selectedDepartment, setSelectedDepartment] = React.useState<string>("");

  const theme = useTheme();
  const dataListRef = React.useRef<IDataListRef | null>(null); 

  const departments =['BDE','CCEC','CCEE','STE','PPC','WEE','MDE','MEE','CEM','EEE','AET','SEE','AEM','IEM'];
  const oldDepartments = ['AET','SEE','IEM','MDE','MEE','CEM','EEE','HEM','CCEC','CCEE','STE','PPCC','PPCP','WEE']
  departments.sort();
  oldDepartments.sort();

  const onClear = () => {
    onFormClear();
    setLecturesCount("0");
    setTutsCount("0");
    setChosenCourse(undefined);
    setSelectedDepartment("");
    if (dataListRef.current) 
      dataListRef.current.onClear();
  }

  const onSubmit = (tst?: IFormResult) => {
    const submitResult: IFormResult = {
      courseID: chosenCourse?.code ? chosenCourse?.code : "" ,
      credits: chosenCourse?.credits ? chosenCourse?.credits  : -1,
      missedLecs: parseInt(lecturesCount),
      missedTuts: parseInt(tutsCount)
    }
    tst ?  onFormSubmit(tst) : onFormSubmit(submitResult);
  }

  const handleDepartmentChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedDepartment(event.target.value as string);
    // Additional logic can be added here to fetch department-specific courses
  };

  const isLecsDisabled = (chosenCourse: ICourse | null) => {
    if (chosenCourse === null) return false;
    const type = courseType(chosenCourse.code, chosenCourse.credits);
    return type === 'passOrFail' || type === '4Crds';
  }

  const isTutsDisabled = (chosenCourse: ICourse | null) => {
    if (chosenCourse === null) return false;
    const type = courseType(chosenCourse.code, chosenCourse.credits);
    return ['passOrFail', 'GEN', '4Crds', '1Crds'].includes(type);
  }

  const handleFourCrds = (chosenCourse: ICourse) => {
    if (isLecsDisabled(chosenCourse) && isTutsDisabled(chosenCourse)) {
      onSubmit({
        courseID: chosenCourse?.code || "",
        credits: chosenCourse?.credits || -1,
        missedLecs: parseInt(lecturesCount),
        missedTuts: parseInt(tutsCount),
      });
    }
  }

  return (
    <Box 
      sx={{
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: '10px',
        background: bylaw === 'Old' ? theme.palette.common.oldBylawColor : theme.palette.common.newBylawColor,
        boxSizing: 'border-box',
        padding: '20px 25px',
        gap: '10px 0px',
        width: '500px',
        height:'fit-content',
        '@media screen and (max-width: 768px)': { width: 'auto', flex: 1 }
      }}
    >
      <Typography sx={{ fontSize: '25px', fontWeight: '500' }}>
        Check Your Absence 
      </Typography>

      {/* Styled Department Select Box */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ minWidth: '30%' }}>Department: <br />({bylaw} Bylaw)</Typography>
        <Box sx={{ width: '70%'}}>
          <FormControl fullWidth>
            <InputLabel id="department-select-label" >Select Department</InputLabel>
            <Select
              labelId="department-select-label"
              id="department-select"
              value={selectedDepartment}
              label="Select Department"
              onChange={(event) => setSelectedDepartment(event.target.value as string)}
            >
              {(bylaw === 'New' ? departments : oldDepartments).map((dept) => (
                <MenuItem key={dept} value={dept}>{dept}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ display: 'flex' }}>
        <Typography sx={{ minWidth: '30%' }}>Course Name: <br />({bylaw} Bylaw)</Typography>
        <DataList 
          ref={dataListRef} 
          bylaw={bylaw}
          department={selectedDepartment}
          onChoose={chosen => { handleFourCrds(chosen); setChosenCourse(chosen); }}
        />
      </Box>

      <Box sx={{ display: 'flex' }}>
        <Typography sx={{ width: '30%' }}>Absent Lecs:</Typography>
        <BasicSelect  
          disabled={isLecsDisabled(chosenCourse || null)}
          options={["0", "1", "2", "3", "4", "5", "6"]} 
          onSelect={setLecturesCount} 
          selectedOption={lecturesCount}
        />
      </Box>

      <Box sx={{ display: 'flex' }}>
        <Typography sx={{ width: '30%' }}>Absent Tuts:</Typography>
        <BasicSelect 
          disabled={isTutsDisabled(chosenCourse || null)}
          options={["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]} 
          onSelect={setTutsCount}
          selectedOption={tutsCount}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button 
          id="clear-button"
          onClick={onClear} 
          sx={{
            background: theme.palette.common.darkBlue,
            color: 'White',
            padding: '5px 20px',
            transition: 'transform 0.2s ease',
            '&:hover': {
              background: theme.palette.common.darkBlue,
              transform: 'scale(1.05)',
              color: 'White',
            }
          }}
        >
          Clear 
        </Button>
        <Button 
          id="check-button"
          onClick={() => onSubmit()}
          sx={{
            background: theme.palette.common.acceptGreen,
            color: 'White',
            padding: '5px 20px',
            transition: 'transform 0.2s ease',
            '&:hover': {
              background: theme.palette.common.acceptGreen,
              transform: 'scale(1.05)',
              color: 'White',
            }
          }}
        >
          Check
        </Button>
      </Box>
    </Box>
  );
}

export default InputForm;
